<template>
  <div>
    <div class="flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
      <h2 v-if="title" class="m-0">{{ title }}</h2>
      <Button
        icon="pi pi-plus"
        label="Nuevo usuario"
        size="small"
        style="margin-left: auto"
        @click="showCreate = true"
      />
    </div>

    <!-- Indicador de presencia en vivo -->
    <div class="card mb-3" v-if="activeUsers.length">
      <div class="flex align-items-center gap-2 mb-2">
        <span class="presence-dot" />
        <span class="font-semibold text-sm">Usuarios activos ahora</span>
      </div>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="u in activeUsers"
          :key="u.uid"
          class="presence-badge"
        >
          <i class="pi pi-user text-xs" />
          <span>{{ u.email }}</span>
          <span class="text-xs text-color-secondary" v-if="u.page"> · {{ pageLabel(u.page) }}</span>
        </div>
      </div>
    </div>

    <!-- Tabla de usuarios -->
    <div class="card">
      <DataTable :value="users" :loading="loading" size="small" stripedRows>
        <Column field="email" header="Usuario" />
        <Column field="role" header="Rol" style="width: 100px">
          <template #body="{ data }">
            <Tag :severity="data.role === 'admin' ? 'warn' : 'secondary'" :value="data.role" />
          </template>
        </Column>
        <Column field="created_at" header="Creado" style="width: 140px">
          <template #body="{ data }">{{ formatDate(data.created_at) }}</template>
        </Column>
        <Column header="En línea" style="width: 80px; text-align:center">
          <template #body="{ data }">
            <span
              v-if="isOnline(data.uid)"
              class="presence-dot-sm"
              v-tooltip="presenceInfo(data.uid)"
            />
          </template>
        </Column>
        <Column header="" style="width: 140px">
          <template #body="{ data }">
            <div class="flex gap-1 justify-content-end">
              <Button
                v-if="data.uid !== userStore.uid"
                :icon="data.role === 'admin' ? 'pi pi-arrow-down' : 'pi pi-arrow-up'"
                size="small"
                severity="secondary"
                text
                v-tooltip="data.role === 'admin' ? 'Quitar admin' : 'Hacer admin'"
                @click="toggleRole(data)"
              />
              <Button
                v-if="data.uid !== userStore.uid"
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                @click="confirmDelete(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Dialog: crear usuario -->
    <Dialog v-model:visible="showCreate" header="Nuevo Usuario" modal style="width: 420px">
      <div class="flex flex-column gap-3 pt-2">
        <div class="flex flex-column gap-1">
          <label>Usuario</label>
          <InputText v-model="newUser.email" placeholder="usuario" autofocus />
        </div>
        <div class="flex flex-column gap-1">
          <label>Contraseña inicial</label>
          <div class="flex gap-2">
            <InputText v-model="newUser.password" placeholder="Contraseña" class="flex-1" />
            <Button icon="pi pi-refresh" severity="secondary" v-tooltip="'Generar aleatoria'" @click="fillGeneratedPassword" />
            <Button icon="pi pi-copy" severity="secondary" v-tooltip="'Copiar'" :disabled="!newUser.password" @click="copyPassword" />
          </div>
        </div>
        <div class="flex flex-column gap-1">
          <label>Rol</label>
          <Select
            v-model="newUser.role"
            :options="roleOptions"
            option-label="label"
            option-value="value"
          />
        </div>
        <div v-if="error" class="text-red-500 text-sm">{{ error }}</div>
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancelar" severity="secondary" @click="showCreate = false" />
          <Button label="Crear" :loading="saving" @click="createUser" />
        </div>
      </div>
    </Dialog>

    <ConfirmDialog />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps } from 'vue'
import { useApi } from '@/composables/useApi'
import { useUserStore } from '@/store/useUserStore'
import { usePresenceStore } from '@/store/usePresenceStore'
import { useAppToast } from '@/composables/useAppToast'
import { generatePassword } from '@/composables/usePasswordGenerator'
import { useConfirm } from 'primevue/useconfirm'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Tag from 'primevue/tag'
import Select from 'primevue/dropdown'
import ConfirmDialog from 'primevue/confirmdialog'

defineProps({
  title: { type: String, default: '' },
})

const api = useApi()
const userStore = useUserStore()
const presenceStore = usePresenceStore()
const confirm = useConfirm()
const toast = useAppToast()

const users = ref([])
const loading = ref(false)
const showCreate = ref(false)
const saving = ref(false)
const error = ref(null)
const newUser = ref({ email: '', password: '', role: 'member' })

const roleOptions = [
  { label: 'Miembro', value: 'member' },
  { label: 'Administrador', value: 'admin' },
]

const pageTitles = {
  salidas: 'Salidas',
  listaTerritorio: 'Territorios',
  mapaTerritorios: 'Mapa',
  estadisticas: 'Informes',
  rutas: 'Rutas',
  tAsignados: 'Asignados',
  parametros: 'Configuración',
  userManagement: 'Usuarios',
}

const activeUsers = computed(() =>
  presenceStore.users.filter((u) => u.uid !== userStore.uid)
)

const isOnline = (uid) => presenceStore.users.some((u) => u.uid === uid)

const presenceInfo = (uid) => {
  const u = presenceStore.users.find((p) => p.uid === uid)
  if (!u) return ''
  return u.page ? `En: ${pageLabel(u.page)}` : 'En línea'
}

const pageLabel = (page) => pageTitles[page] || page

const formatDate = (dt) => {
  if (!dt) return '-'
  return new Date(dt).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const loadUsers = async () => {
  loading.value = true
  try {
    const result = await api.get('/api/congregation/users')
    users.value = result.users || []
  } finally {
    loading.value = false
  }
}

const createUser = async () => {
  if (!newUser.value.email || !newUser.value.password) {
    error.value = 'Usuario y contraseña son requeridos'
    return
  }
  saving.value = true
  error.value = null
  try {
    const result = await api.post('/api/congregation/users', {
      email: newUser.value.email,
      password: newUser.value.password,
      role: newUser.value.role,
    })
    if (result.error) { error.value = result.error; return }
    showCreate.value = false
    newUser.value = { email: '', password: '', role: 'member' }
    await loadUsers()
  } finally {
    saving.value = false
  }
}

const fillGeneratedPassword = () => {
  newUser.value.password = generatePassword()
}

const copyPassword = async () => {
  try {
    await navigator.clipboard.writeText(newUser.value.password)
    toast.success('Contraseña copiada')
  } catch {
    toast.error('No se pudo copiar', 'Copiala manualmente desde el campo.')
  }
}

const toggleRole = async (user) => {
  const newRole = user.role === 'admin' ? 'member' : 'admin'
  await api.put(`/api/congregation/users/${user.uid}`, { role: newRole })
  await loadUsers()
}

const confirmDelete = (user) => {
  confirm.require({
    message: `¿Eliminar al usuario "${user.email}"?`,
    header: 'Confirmar eliminación',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      await api.del(`/api/congregation/users/${user.uid}`)
      await loadUsers()
    },
  })
}

onMounted(loadUsers)
</script>

<style scoped>
.card {
  background: var(--bg-primary);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  box-shadow: var(--shadow-1);
  text-align: left;
}

.presence-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--success);
  animation: pulse 2s infinite;
}

.presence-dot-sm {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--success);
}

.presence-badge {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  background: var(--success-soft);
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  padding: 3px 10px;
  font-size: var(--text-caption);
  color: var(--success);
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
